// ฟังก์ชันสำหรับจัดการข้อมูล Store Verification ใน Firestore

import { 
  collection, 
  doc, 
  setDoc, 
  getDoc, 
  updateDoc, 
  query, 
  where, 
  orderBy, 
  getDocs,
  serverTimestamp,
  deleteDoc,
  Timestamp
} from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL, deleteObject } from 'firebase/storage';
import { db, storage } from '../firebase/firebaseConfig';
import { createVerificationModel, createAdminVideoCallBookingModel } from '../data/verificationModels';
import { calculateVerificationScore, calculateRiskScore } from '../data/verificationModels';

const VERIFICATIONS_COLLECTION = 'storeVerifications';
const ADMIN_VIDEO_CALL_BOOKINGS_COLLECTION = 'adminVideoCallBookings';
const SELFIE_STORAGE_PATH = 'verifications/selfies';
const DOCUMENTS_STORAGE_PATH = 'verifications/documents';

/**
 * สร้างการตรวจสอบใหม่
 */
export const createVerification = async (storeId, ownerId, verificationData) => {
  try {
    const verificationRef = doc(collection(db, VERIFICATIONS_COLLECTION));
    const verification = createVerificationModel({
      ...verificationData,
      storeId,
      ownerId,
      id: verificationRef.id,
      submittedAt: serverTimestamp(),
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
    });
    
    await setDoc(verificationRef, verification);
    return { success: true, verificationId: verificationRef.id, verification };
  } catch (error) {
    console.error('Error creating verification:', error);
    return { success: false, error: error.message };
  }
};

/**
 * อัพโหลด Selfie Image
 */
export const uploadSelfieImage = async (file, verificationId) => {
  try {
    const storageRef = ref(storage, `${SELFIE_STORAGE_PATH}/${verificationId}/${Date.now()}_${file.name}`);
    await uploadBytes(storageRef, file);
    const downloadURL = await getDownloadURL(storageRef);
    
    // คำนวณวันที่ลบอัตโนมัติ (30 วัน)
    const autoDeleteAt = new Date();
    autoDeleteAt.setDate(autoDeleteAt.getDate() + 30);
    
    // อัพเดท verification document
    const verificationRef = doc(db, VERIFICATIONS_COLLECTION, verificationId);
    await updateDoc(verificationRef, {
      'selfieVerification.imageUrl': downloadURL,
      'selfieVerification.uploadedAt': serverTimestamp(),
      'selfieVerification.autoDeleteAt': Timestamp.fromDate(autoDeleteAt),
      updatedAt: serverTimestamp(),
    });
    
    return { success: true, imageUrl: downloadURL };
  } catch (error) {
    console.error('Error uploading selfie image:', error);
    return { success: false, error: error.message };
  }
};

/**
 * อัพโหลดเอกสารประกอบ
 */
export const uploadSupportingDocument = async (file, verificationId, documentType) => {
  try {
    const validTypes = ['commercialRegistration', 'cannabisLicense', 'addressProof', 'taxProof'];
    if (!validTypes.includes(documentType)) {
      return { success: false, error: 'Invalid document type' };
    }
    
    const storageRef = ref(storage, `${DOCUMENTS_STORAGE_PATH}/${verificationId}/${documentType}_${Date.now()}_${file.name}`);
    await uploadBytes(storageRef, file);
    const downloadURL = await getDownloadURL(storageRef);
    
    // อัพเดท verification document
    const verificationRef = doc(db, VERIFICATIONS_COLLECTION, verificationId);
    await updateDoc(verificationRef, {
      [`supportingDocuments.${documentType}.uploaded`]: true,
      [`supportingDocuments.${documentType}.imageUrl`]: downloadURL,
      [`supportingDocuments.${documentType}.status`]: 'pending',
      updatedAt: serverTimestamp(),
    });
    
    return { success: true, imageUrl: downloadURL };
  } catch (error) {
    console.error('Error uploading supporting document:', error);
    return { success: false, error: error.message };
  }
};

/**
 * ดึงข้อมูลการตรวจสอบ
 */
export const getVerification = async (verificationId) => {
  try {
    const verificationRef = doc(db, VERIFICATIONS_COLLECTION, verificationId);
    const verificationSnap = await getDoc(verificationRef);
    
    if (!verificationSnap.exists()) {
      return { success: false, error: 'Verification not found' };
    }
    
    return { success: true, verification: { id: verificationSnap.id, ...verificationSnap.data() } };
  } catch (error) {
    console.error('Error getting verification:', error);
    return { success: false, error: error.message };
  }
};

/**
 * ดึงข้อมูลการตรวจสอบตาม storeId
 */
export const getVerificationByStoreId = async (storeId) => {
  try {
    // Query โดยใช้ storeId และ orderBy เท่านั้น (ไม่ใช้ where status เพื่อหลีกเลี่ยง composite index)
    const q = query(
      collection(db, VERIFICATIONS_COLLECTION),
      where('storeId', '==', storeId),
      orderBy('createdAt', 'desc')
    );
    
    const querySnapshot = await getDocs(q);
    
    if (querySnapshot.empty) {
      return { success: false, error: 'No verification found' };
    }
    
    // Filter ใน code แทนการใช้ where('status', 'in', ...) เพื่อหลีกเลี่ยง composite index
    const validStatuses = ['pending', 'in_review', 'approved'];
    const verificationDoc = querySnapshot.docs.find(doc => {
      const data = doc.data();
      return validStatuses.includes(data.status);
    });
    
    if (!verificationDoc) {
      return { success: false, error: 'No active verification found' };
    }
    
    const verification = {
      id: verificationDoc.id,
      ...verificationDoc.data()
    };
    
    return { success: true, verification };
  } catch (error) {
    console.error('Error getting verification by storeId:', error);
    // ถ้ายังมี error เรื่อง index ให้ลอง query แบบง่ายๆ
    try {
      const q = query(
        collection(db, VERIFICATIONS_COLLECTION),
        where('storeId', '==', storeId)
      );
      const querySnapshot = await getDocs(q);
      
      if (querySnapshot.empty) {
        return { success: false, error: 'No verification found' };
      }
      
      // Sort และ filter ใน code
      const docs = querySnapshot.docs
        .map(doc => ({ id: doc.id, ...doc.data() }))
        .filter(v => ['pending', 'in_review', 'approved'].includes(v.status))
        .sort((a, b) => {
          const aDate = a.createdAt?.toDate?.() || new Date(a.createdAt || 0);
          const bDate = b.createdAt?.toDate?.() || new Date(b.createdAt || 0);
          return bDate.getTime() - aDate.getTime();
        });
      
      if (docs.length === 0) {
        return { success: false, error: 'No active verification found' };
      }
      
      return { success: true, verification: docs[0] };
    } catch (fallbackError) {
      console.error('Error in fallback query:', fallbackError);
      return { success: false, error: fallbackError.message };
    }
  }
};

/**
 * อัพเดทสถานะการตรวจสอบ (Admin)
 */
export const updateVerificationStatus = async (verificationId, status, adminId, notes = '') => {
  try {
    const verificationRef = doc(db, VERIFICATIONS_COLLECTION, verificationId);
    const verificationSnap = await getDoc(verificationRef);
    
    if (!verificationSnap.exists()) {
      return { success: false, error: 'Verification not found' };
    }
    
    const verification = verificationSnap.data();
    const updateData = {
      status,
      reviewedBy: adminId,
      reviewNotes: notes,
      updatedAt: serverTimestamp(),
    };
    
    if (status === 'approved') {
      updateData.approvedAt = serverTimestamp();
      // คำนวณคะแนนใหม่
      const newVerification = { ...verification, ...updateData };
      const score = calculateVerificationScore(newVerification);
      const riskScore = calculateRiskScore(newVerification);
      
      updateData.verificationScore = score;
      updateData.riskScore = riskScore;
      
      // กำหนดวันหมดอายุ (1 ปี)
      const expiresAt = new Date();
      expiresAt.setFullYear(expiresAt.getFullYear() + 1);
      updateData.expiresAt = Timestamp.fromDate(expiresAt);
    } else if (status === 'rejected') {
      updateData.rejectedAt = serverTimestamp();
    }
    
    await updateDoc(verificationRef, updateData);
    
    // อัพเดท store document
    if (status === 'approved') {
      const storeRef = doc(db, 'stores', verification.storeId);
      await updateDoc(storeRef, {
        'verification.status': 'approved',
        'verification.verificationId': verificationId,
        'verification.verificationScore': updateData.verificationScore,
        'verification.riskScore': updateData.riskScore,
        'verification.verifiedAt': serverTimestamp(),
        'verification.expiresAt': updateData.expiresAt,
        isVerified: true,
        updatedAt: serverTimestamp(),
      });
    }
    
    return { success: true };
  } catch (error) {
    console.error('Error updating verification status:', error);
    return { success: false, error: error.message };
  }
};

/**
 * อัพเดทสถานะ Selfie Verification (Admin)
 */
export const updateSelfieVerificationStatus = async (verificationId, status, adminId, notes = '') => {
  try {
    const verificationRef = doc(db, VERIFICATIONS_COLLECTION, verificationId);
    const updateData = {
      'selfieVerification.status': status,
      'selfieVerification.reviewedAt': serverTimestamp(),
      'selfieVerification.reviewedBy': adminId,
      'selfieVerification.reviewNotes': notes,
      updatedAt: serverTimestamp(),
    };
    
    await updateDoc(verificationRef, updateData);
    
    // คำนวณคะแนนใหม่
    const verificationSnap = await getDoc(verificationRef);
    const verification = verificationSnap.data();
    const score = calculateVerificationScore(verification);
    const riskScore = calculateRiskScore(verification);
    
    await updateDoc(verificationRef, {
      verificationScore: score,
      riskScore,
      updatedAt: serverTimestamp(),
    });
    
    return { success: true };
  } catch (error) {
    console.error('Error updating selfie verification status:', error);
    return { success: false, error: error.message };
  }
};

/**
 * อัพเดทสถานะเอกสารประกอบ (Admin)
 */
export const updateDocumentStatus = async (verificationId, documentType, status, adminId, notes = '') => {
  try {
    const validTypes = ['commercialRegistration', 'cannabisLicense', 'addressProof', 'taxProof'];
    if (!validTypes.includes(documentType)) {
      return { success: false, error: 'Invalid document type' };
    }
    
    const scores = {
      commercialRegistration: 20,
      cannabisLicense: 30,
      addressProof: 10,
      taxProof: 15,
    };
    
    const verificationRef = doc(db, VERIFICATIONS_COLLECTION, verificationId);
    const updateData = {
      [`supportingDocuments.${documentType}.status`]: status,
      [`supportingDocuments.${documentType}.reviewedAt`]: serverTimestamp(),
      [`supportingDocuments.${documentType}.reviewedBy`]: adminId,
      [`supportingDocuments.${documentType}.reviewNotes`]: notes,
      updatedAt: serverTimestamp(),
    };
    
    if (status === 'approved') {
      updateData[`supportingDocuments.${documentType}.score`] = scores[documentType];
    } else {
      updateData[`supportingDocuments.${documentType}.score`] = 0;
    }
    
    await updateDoc(verificationRef, updateData);
    
    // คำนวณคะแนนใหม่
    const verificationSnap = await getDoc(verificationRef);
    const verification = verificationSnap.data();
    const score = calculateVerificationScore(verification);
    const riskScore = calculateRiskScore(verification);
    
    await updateDoc(verificationRef, {
      verificationScore: score,
      riskScore,
      updatedAt: serverTimestamp(),
    });
    
    return { success: true };
  } catch (error) {
    console.error('Error updating document status:', error);
    return { success: false, error: error.message };
  }
};

/**
 * ลบ Selfie Image (Auto-delete)
 */
export const deleteSelfieImage = async (verificationId, imageUrl) => {
  try {
    // ลบจาก Storage
    if (imageUrl) {
      const imageRef = ref(storage, imageUrl);
      await deleteObject(imageRef);
    }
    
    // อัพเดท verification document
    const verificationRef = doc(db, VERIFICATIONS_COLLECTION, verificationId);
    await updateDoc(verificationRef, {
      'selfieVerification.imageUrl': '',
      'selfieVerification.autoDeleteAt': null,
      updatedAt: serverTimestamp(),
    });
    
    return { success: true };
  } catch (error) {
    console.error('Error deleting selfie image:', error);
    return { success: false, error: error.message };
  }
};

/**
 * สร้างการจอง Video Call กับแอดมิน
 */
export const createAdminVideoCallBooking = async (bookingData) => {
  try {
    const bookingRef = doc(collection(db, ADMIN_VIDEO_CALL_BOOKINGS_COLLECTION));
    const booking = createAdminVideoCallBookingModel({
      ...bookingData,
      id: bookingRef.id,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
    });
    
    await setDoc(bookingRef, booking);
    return { success: true, bookingId: bookingRef.id, booking };
  } catch (error) {
    console.error('Error creating admin video call booking:', error);
    return { success: false, error: error.message };
  }
};

/**
 * ดึงรายการ Video Call Bookings (Admin)
 */
export const getAdminVideoCallBookings = async (status = null) => {
  try {
    let q = query(
      collection(db, ADMIN_VIDEO_CALL_BOOKINGS_COLLECTION),
      orderBy('createdAt', 'desc')
    );
    
    if (status) {
      q = query(q, where('status', '==', status));
    }
    
    const querySnapshot = await getDocs(q);
    const bookings = querySnapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    }));
    
    return { success: true, bookings };
  } catch (error) {
    console.error('Error getting admin video call bookings:', error);
    return { success: false, error: error.message };
  }
};

/**
 * อัพเดทสถานะ Video Call Booking (Admin)
 */
export const updateVideoCallBookingStatus = async (bookingId, status, adminId, reason = '') => {
  try {
    const bookingRef = doc(db, ADMIN_VIDEO_CALL_BOOKINGS_COLLECTION, bookingId);
    const updateData = {
      status,
      updatedAt: serverTimestamp(),
    };
    
    if (status === 'approved') {
      updateData.approvedAt = serverTimestamp();
      updateData.approvedBy = adminId;
    } else if (status === 'rejected') {
      updateData.rejectedAt = serverTimestamp();
      updateData.rejectedBy = adminId;
      updateData.rejectionReason = reason;
    }
    
    await updateDoc(bookingRef, updateData);
    return { success: true };
  } catch (error) {
    console.error('Error updating video call booking status:', error);
    return { success: false, error: error.message };
  }
};


